<template>
  <slide-out
    dock="right"
    :size="sideOutCurrentSize"
    style="z-index: 200;"
    ref="facilitySlideout"
    @resize="OnResize"
    :min-size="sideOutMinSize"
    :allow-resize="nested ? false : true"
    :append-to="nested ? '' : '#app'"
    fixed
    :visible.sync="slideouts.active"
    v-on:close="onSlideoutClosing">
    <template v-slot:header>
      <h3 v-if="isFetching" class="font-weight-bold pa-1" small>
        <v-progress-circular :size="16" width="2" indeterminate class="mr-1"></v-progress-circular>
        Fetching Facility ...
      </h3>
      <h3 v-else-if="selected.id" class="font-weight-bold pa-1" small>
        <v-icon left>mdi-pencil-box-outline</v-icon> Update: '{{ selected.name }}'
      </h3>
      <h3 v-else class="font-weight-bold pa-1" small>
        <v-icon left>mdi-plus-box-outline</v-icon> Add Facility
      </h3>
      <v-btn @click="closeUpdateSlideout()" icon>
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </template>

    <div>
      <div class="text-center d-flex flex-column" v-if="isFetching">
        <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
        <p class="font-weight-medium">Loading Facility...</p>
      </div>
      <v-form v-else class="pa-4" ref="updateForm" v-model="valid">
        <SingleImageUploader
          id="facility-uploader"
          apiUrl="api/Facilities/Image"
          class="mb-4"
          v-model="selected.imageUrl"></SingleImageUploader>
        <v-text-field
          dense
          outlined
          v-model="selected.name"
          :rules="[formRules.required]"
          label="Facility Name"
          placeholder="Type Facility Name"></v-text-field>
        <ClientSelector
          v-model="selected.clientId"
          label="Client"
          placeholder="Select or create a client"
          :companyId="company?.id"
          class="mb-4" />
        
        <p class="text-caption font-italic text--secondary mt-4" style="margin-top: -8px; line-height: 1.3;">
          Groups assets into a single entity when treated as separate facilities in AVA, ensuring tag information is consistently applied across all grouped assets.
        </p>
        <v-select
          dense
          outlined
          v-model="selected.facilityGroupId"
          :items="facilityGroups"
          item-text="name"
          item-value="id"
          label="Facility Grouping"
          placeholder="Select a facility group (optional)"
          clearable
          class="mb-2">
        </v-select>
                
        <v-label class="black--text mb-2 d-block">Address</v-label>
        <v-text-field
          dense
          outlined
          v-model="selected.address.line1"
          placeholder="Company Address"></v-text-field>
        <v-row>
          <v-col>
            <v-label class="black--text mb-2 d-block">City</v-label>
            <v-text-field
              dense
              outlined
              v-model="selected.address.city"
              placeholder="City"></v-text-field>
          </v-col>
          <v-col>
            <v-label class="black--text mb-2 d-block">State</v-label>
            <v-text-field
              dense
              outlined
              v-model="selected.address.state"
              placeholder="State"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-label class="black--text mb-2 d-block">Country</v-label>
            <v-text-field
              dense
              outlined
              v-model="selected.address.country"
              placeholder="Country"></v-text-field>
          </v-col>
        </v-row>
        
        <!-- Limits Section -->
        <v-expansion-panels class="mt-4">
          <v-expansion-panel>
            <v-expansion-panel-header class="font-weight-medium">Limits & Usage</v-expansion-panel-header>
            <v-expansion-panel-content>
              <FacilityLimits 
                :facility-data="selected" 
                :company="company"
                @update:smallModelsLimitCount="updateSmallModelsLimit"
                @update:scanUploadLimit="updateScanUploadLimit"
                @update:scanDownloadLimit="updateScanDownloadLimit"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-form>
    </div>
    <template v-slot:footer>
      <v-card-actions>
        <v-btn text color="error" @click="closeUpdateSlideout()">
          <v-icon left>mdi-chevron-left</v-icon> Close
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :disabled="!valid || slideouts.isLoading || !slideouts.hasChanges"
          @click="updateConfirmed()"
          :loading="slideouts.isLoading">
          <v-icon left>{{ selected.id ? 'mdi-check' : 'mdi-plus' }}</v-icon>
          {{ selected.id ? 'Save Changes' : 'Create' }}
        </v-btn>
      </v-card-actions>
    </template>
  </slide-out>
</template>

<script>
  import formRules from '@Share/config/form-rules';
  import { $handleError, $isEqual } from '@/utils';
  import moment from 'dayjs';
  import SingleImageUploader from '@Share/components/SingleImageUploader.vue';
  import ClientSelector from '@/features/Client/components/ClientSelector.vue';
  import FacilityLimits from '@/features/Facility/components/FacilityLimits.vue';
  import managementFacilitiesService from '@/features/Company/service/management-facilities.service.js';
  import groupsService from '@/features/Groups/service/groups.service.js';
  import { error } from '@/utils/debugLogger';

  export default {
    name: 'edit-Facility',
    data() {
      return {
        valid: false,
        formRules,
        isFetching: false,
        selected: { address: {} },
        modal: false,
        selectedCemented: { address: {} },
        facilityGroups: [],
        company: null,
        slideouts: {
          active: false,
          isLoading: false,
          isClosing: false,
          hasChanges: false,
        },
      };
    },
    props: {
      nested: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      createdDateAsText() {
        if (this.selected == null) return 'N/A';
        return moment(this.selected.createDate).format('YYYY/MM/DD, hh:mm A');
      },
      sideOutMinSize: {
        get() {
          return this.$store.getters['GET_SIDOUT_MINSIZE'];
        },
        set(val) {
          this.$store.commit('SET_SIDOUT_MINSIZE', val);
        },
      },
      sideOutCurrentSize: {
        get() {
          return this.$store.getters['GET_SIDOUT_CURRENTSIZE'];
        },
        set(val) {
          this.$store.commit('SET_SIDOUT_CURRENTSIZE', val);
        },
      },
    },
    methods: {
      OnResize() {
        let facilitySlideout = this.$refs.facilitySlideout;
        this.sideOutCurrentSize = facilitySlideout.resizeValue;
      },
      onSlideoutClosing() {
        this.$emit('close');
        this.closeUpdateSlideout();
      },
      updateConfirmed() {
        if (!this.company || !this.company.id) {
          this.$dialog.notify.error('Company information is required', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }
        
        this.slideouts.isLoading = true;
        let toSend = structuredClone(this.selected);
        
        const operation = toSend.id 
          ? managementFacilitiesService.update(this.company.id, toSend)
          : managementFacilitiesService.add(this.company.id, toSend);
          
        operation
          .then(() => {
            var message = toSend.id 
              ? 'Facility updated successfully!'
              : 'Facility added successfully!';
              
            this.closeUpdateSlideout();
            this.$dialog.notify.success(message, {
              position: 'top-right',
              timeout: 3000,
            });
            
            // Emit event to refresh facility list
            this.$emit('facility-updated');
          })
          .catch((err) => {
            $handleError(err);
          })
          .finally(() => {
            this.slideouts.isLoading = false;
          });
      },
         
    closeUpdateSlideout() {
      this.selected = { address: {} };
      this.isFetching = false;
      this.cementFacility();
      this.slideouts.active = false;
      this.$refs.updateForm?.resetValidation();
    },
    open(company, id) {
      this.company = company;
      if (id != null) {
        this.FacilityId = id;
        this.fetchFacility();
      } else {
        this.selected = { 
          address: {},
          smallModelsLimitCount: 0,
          scanUploadLimit: 0,
          scanDownloadLimit: 0
        };
      }
      // Pass company ID when fetching clients
      this.$store.dispatch('CLIENT/GET_CLIENTS', company?.id);
      this.loadFacilityGroups();
      this.slideouts.active = true;
    },
    fetchFacility() {
      this.isFetching = true;
      managementFacilitiesService.getById(this.company.id, this.FacilityId)
        .then((resp) => {
          let data = structuredClone(resp.data);
          if (!data.address) data.address = {};
          
          // Ensure limit fields exist with default values if not present
          if (data.smallModelsLimitCount === undefined) data.smallModelsLimitCount = 0;
          if (data.scanUploadLimit === undefined) data.scanUploadLimit = 0;
          if (data.scanDownloadLimit === undefined) data.scanDownloadLimit = 0;
          
          this.selected = data;
          
        })
        .catch((err) => {
          $handleError(err);
        })
        .finally(() => {
          this.isFetching = false;
        });
    },
    cementFacility() {
      this.selectedCemented = structuredClone(this.selected);
      this.checkForChanges();
    },
    checkForChanges() {
      if (!this.slideouts.active) return;

      this.slideouts.hasChanges = !$isEqual(this.selected, this.selectedCemented);
    },
    loadFacilityGroups() {
      // Load facility groups directly using the service
      if (!this.company || !this.company.id) {
        this.facilityGroups = [];
        return;
      }
      
      groupsService.getByCompanyId(this.company.id)
        .then(({ data }) => {
          this.facilityGroups = data.items || [];
        })
        .catch((err) => {
          error('ManagementEditFacility', 'Failed to load facility groups', err);
          this.facilityGroups = [];
        });
    },
    // Methods to handle updates from FacilityLimits component
    updateSmallModelsLimit(value) {
      this.$set(this.selected, 'smallModelsLimitCount', value);
    },
    updateScanUploadLimit(value) {
      this.$set(this.selected, 'scanUploadLimit', value);
    },
    updateScanDownloadLimit(value) {
      this.$set(this.selected, 'scanDownloadLimit', value);
    },
  },
  components: {
    SingleImageUploader,
    ClientSelector,
    FacilityLimits,
  },
  watch: {
    selected: {
      handler() {
        this.checkForChanges();
      },
      deep: true,
    },
    nested: {
      handler() {
        this.nested ? (this.sideOutCurrentSize = '80vw') : (this.sideOutCurrentSize = '35vw');
        this.nested ? (this.sideOutMinSize = '80vw') : (this.sideOutMinSize = '35vw');
      },
      deep: true,
    }
  }
}
  
</script>
